import { ErrorItem } from '@marketplace-web/core-api/api-client-util'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
export type UserTarget =
  | 'login'
  | 'login_with_facebook'
  | 'login_with_google'
  | 'login_with_apple'
  | 'switch_to_login'
  | 'switch_to_registration'
  | 'switch_to_email_login'
  | 'switch_to_email_registration'
  | 'close_select_type_modal'
  | 'terms_and_conditions'
  | 'privacy_policy'
  | 'having_trouble'
  | 'social_login_with_password'
  | 'forgot_password'
  | 'use_suggested_username'
  | 'submit_suggested_username'
  | 'newsletter_checkbox'
  | 'policies_checkbox'
  | 'email'
  | 'register_with_social'
  | 'register_with_email'
  // TODO: Add it to the dwh-schema-registry
  | 'switch_to_reset_password'
  // TODO: Add it to the dwh-schema-registry
  | 'full_name'
  // TODO: Add it to the dwh-schema-registry
  | 'username'
  // TODO: Add it to the dwh-schema-registry
  | 'password'
  // TODO: Add it to the dwh-schema-registry
  | 'zip_code'

export type RegistrationBehaviourAction = 'start' | 'click' | 'submit' | 'end'

export type RegistrationBehaviourClickActionDetails =
  | 'privacy-policy-link'
  | 'terms-and-conditions-link'
  | 'registration-faq-link'

export type RegistrationBehaviourEndActionDetails = 'modal-closed' | 'page-closed'

type RegistrationBehaviourSubmitActionDetails = 'successful' | 'unsuccessful'

export type RegistrationBehaviourActionDetails =
  | RegistrationBehaviourClickActionDetails
  | RegistrationBehaviourSubmitActionDetails
  | RegistrationBehaviourEndActionDetails

export type RegistrationType = 'email' | 'apple' | 'google' | 'facebook'

type AuthType = 'internal' | 'facebook' | 'google' | 'apple'

type UserRegisterSuccessEventArgs = {
  type: AuthType
  userId?: number
}

type UserRegisterSuccessEventExtra = {
  type: AuthType
}

export const userRegisterSuccessEvent = (args: UserRegisterSuccessEventArgs) => {
  const { type, userId } = args

  const extra: UserRegisterSuccessEventExtra = { type }

  return { event: 'user.register_success', extra, userId }
}

type UserRegisterFailEventExtra = {
  type: AuthType
  reason: string
  details?: string
}

type UserRegisterFailEventArgs = {
  type: AuthType
  errors?: Array<ErrorItem>
}

export const userRegisterFailEvent = (args: UserRegisterFailEventArgs) => {
  const { type, errors } = args

  const extra: UserRegisterFailEventExtra = {
    type,
    reason: 'validation_error',
    details: errors?.map(error => error.value).join(', '),
  }

  return { event: 'user.register_fail', extra }
}

type AuthenticateSuccessEventArgs = {
  type: AuthType
  userId?: number
  country?: string
}

type AuthenticateSuccessEventExtra = {
  type: AuthType
  country?: string
}

export const authenticateSuccessEvent = (args: AuthenticateSuccessEventArgs) => {
  const { type, userId, country } = args

  const extra: AuthenticateSuccessEventExtra = { type, country }

  return { event: 'user.authenticate_success', extra, userId }
}

type AuthenticateFailEventExtra = {
  type: AuthType
  reason: string
  details?: string
  country?: string
}

type AuthenticateFailEventArgs = {
  type: AuthType
  errors?: Array<ErrorItem>
  error?: string
  country?: string
}

export const authenticateFailEvent = (args: AuthenticateFailEventArgs) => {
  const { type, errors, error, country } = args

  const extra: AuthenticateFailEventExtra = {
    type,
    reason: 'validation_error',
    details: errors?.map(({ value }) => value).join(', ') || error,
    country,
  }

  return { event: 'user.authenticate_fail', extra }
}

type RegistrationFormBehaviourEventArgs = {
  taskId: string
  fingerprint: string
  countryCode: string
  screen: string
  authenticationType: RegistrationType
  action: RegistrationBehaviourAction
  actionDetails?: RegistrationBehaviourActionDetails
  inApp: boolean
  userId?: number
}

export const registrationFormBehaviourEvent = ({
  taskId,
  fingerprint,
  countryCode,
  screen,
  authenticationType,
  action,
  actionDetails,
  inApp,
  userId,
}: RegistrationFormBehaviourEventArgs) => {
  return {
    event: 'registration.form_behaviour',
    userId,
    extra: {
      task_id: taskId,
      screen,
      authentication_type: authenticationType,
      action,
      in_app: inApp,
      country_code: countryCode,
      fingerprint,
      action_details: actionDetails,
    },
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs) => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs) => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type UserInputEventArgs = {
  screen?: string
  target: string
  value?: string | null
  state: 'focus' | 'unfocus'
  tempUuid?: string
  isDynamic?: boolean
}

export const userInputEvent = (args: UserInputEventArgs) => {
  const { state, target, value, screen, tempUuid, isDynamic } = args
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false,
  }

  return {
    event: 'user.input',
    extra,
  }
}
